// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useMutation, type UseMutationResult } from '@tanstack/react-query';

import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';

import type { ResultadoDaBuscaDeTitular, TitularDeTerceiro } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados de "Inscrever terceiros": só leitura (ver a justificativa
 * em `../comum/mock.ts`).
 *
 * A busca é `useMutation`, não `useQuery`: não deve disparar sozinha por
 * causa de uma mudança de estado (o CPF digitado, por exemplo), só quando a
 * pessoa clica em "Consultar". Não há ação de gravação aqui: "Concluir
 * inscrição" só mostra o aviso de indisponibilidade na tela.
 */

const TITULARES = dadosMock.titulares as TitularDeTerceiro[];

/**
 * Decide qual dos quatro achados possíveis a busca representa: sem titular
 * nenhum bate com o CPF; ou o titular foi localizado mas falta um dado do
 * qual a inscrição depende (e-mail, telefone, licença ativa — nesta ordem
 * de checagem); ou está tudo completo.
 */
function avaliarResultado(titular: TitularDeTerceiro | null): ResultadoDaBuscaDeTitular {
  if (!titular) {
    return { situacao: 'nao_encontrado', titular: null };
  }
  if (!titular.email) {
    return { situacao: 'sem_email', titular };
  }
  if (!titular.telefone) {
    return { situacao: 'sem_telefone', titular };
  }
  if (!titular.licenca_ativa) {
    return { situacao: 'sem_licenca_ativa', titular };
  }
  return { situacao: 'encontrado', titular };
}

/**
 * Busca do titular pelo CPF — simula a consulta a um cadastro externo (o
 * SGTP, no protótipo). O resultado sempre tem uma `situacao`: a consulta
 * funcionou, mesmo quando não permite seguir direto para a inscrição.
 */
export function useBuscarTitularPorCpf(): UseMutationResult<
  ResultadoDaBuscaDeTitular,
  Error,
  string
> {
  return useMutation<ResultadoDaBuscaDeTitular, Error, string>({
    mutationFn: (cpf) => {
      const cpfLimpo = cpf.replace(/\D/g, '');
      const titular = TITULARES.find((candidato) => candidato.cpf === cpfLimpo) ?? null;
      return resolverMock(avaliarResultado(titular));
    },
  });
}
